/**
 * Form Store
 *
 * Manages form state and submissions
 */

import { create } from 'zustand';

export interface FormState {
  formID: string;
  data: Record<string, unknown>;
  errors: Record<string, string>;
  isSubmitting: boolean;
  isValid: boolean;
}

export interface FormStore {
  forms: Record<string, FormState>;
  setFormData: (formID: string, data: Record<string, unknown>) => void;
  getFormData: (formID: string) => Record<string, unknown>;
  setFormError: (formID: string, field: string, error: string) => void;
  clearFormErrors: (formID: string) => void;
  setFormSubmitting: (formID: string, isSubmitting: boolean) => void;
  resetForm: (formID: string) => void;
  getForm: (formID: string) => FormState | undefined;
}

const emptyForm = (formID: string): FormState => ({
  formID,
  data: {},
  errors: {},
  isSubmitting: false,
  isValid: true,
});

export const useFormStore = create<FormStore>((set, get) => ({
  forms: {},

  setFormData: (formID: string, data: Record<string, unknown>) =>
    set((state) => ({
      forms: {
        ...state.forms,
        [formID]: { ...(state.forms[formID] ?? emptyForm(formID)), data },
      },
    })),

  getFormData: (formID: string) => get().forms[formID]?.data ?? {},

  setFormError: (formID: string, field: string, error: string) =>
    set((state) => {
      const form = state.forms[formID] ?? emptyForm(formID);
      return {
        forms: {
          ...state.forms,
          [formID]: { ...form, errors: { ...form.errors, [field]: error }, isValid: false },
        },
      };
    }),

  clearFormErrors: (formID: string) =>
    set((state) => {
      const form = state.forms[formID];
      if (!form) return {};
      return { forms: { ...state.forms, [formID]: { ...form, errors: {}, isValid: true } } };
    }),

  setFormSubmitting: (formID: string, isSubmitting: boolean) =>
    set((state) => {
      const form = state.forms[formID];
      if (!form) return {};
      return { forms: { ...state.forms, [formID]: { ...form, isSubmitting } } };
    }),

  resetForm: (formID: string) =>
    set((state) => ({
      forms: { ...state.forms, [formID]: emptyForm(formID) },
    })),

  getForm: (formID: string) => get().forms[formID],
}));
